
import { useState, useEffect, useRef } from 'react';
import { auth } from '../services/firebase-config';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';

// Custom hook to handle Firebase anonymous authentication initialization.
// If Anonymous Auth isn't enabled in Firebase Console yet, the error is
// suppressed cleanly — watchlist is disabled but the rest of the app works.
export function useAuthInit() {
  const [authState, setAuthState] = useState({
    isReady: false,
    user: null,
    error: null
  });
  const initRef = useRef(false);

  useEffect(() => {
    // Prevent double-init in React StrictMode
    if (initRef.current) return;
    initRef.current = true;

    let unsubscribe;

    async function initializeAuth() {
      try {
        unsubscribe = onAuthStateChanged(auth, (user) => {
          setAuthState({ isReady: true, user, error: null });
        });

        if (!auth.currentUser) {
          await signInAnonymously(auth);
        } else {
          setAuthState({ isReady: true, user: auth.currentUser, error: null });
        }
      } catch (error) {
        // auth/configuration-not-found = Anonymous Auth not enabled yet.
        // Log a single friendly hint instead of a noisy error stack.
        if (error.code === 'auth/configuration-not-found') {
          console.info(
            '[Auth] Anonymous Authentication is not enabled in Firebase.\n' +
            'Watchlist will be unavailable until you enable it:\n' +
            'Firebase Console → Authentication → Sign-in method → Anonymous → Enable'
          );
        } else {
          console.warn('[Auth] Firebase auth unavailable:', error.message);
        }
        setAuthState({ isReady: true, user: null, error: error.message });
      }
    }

    initializeAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return authState;
}
